import React, { useState, useEffect } from 'react';
import { Modal, Table, Input, Button, Space, message } from 'antd';
import { SearchOutlined, UserOutlined } from '@ant-design/icons';
import CategoryApiService from '../../services/CategoryApiService';
import { useDebounceSearch } from '../../utils/debounce';
import styles from '../../styles/AssignUserModal.module.css';

const { Search } = Input;

const AssignCategoryModal = ({
  visible,
  onCancel,
  onOk,
  confirmLoading,
  categoryId,
  currentAssignedUsers = [],
  users = [],
  onSearch,
  loading
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [initialSelectedKeys, setInitialSelectedKeys] = useState([]);
  const [search, setSearch] = useState('');

  // Use the new debounce hook
  const { debouncedSearch } = useDebounceSearch((value) => {
    onSearch?.(value);
  });

  // Reset states when modal becomes visible or invisible
  useEffect(() => {
    if (!visible) {
      setSearch(''); // Reset search text when modal closes
      onSearch?.(''); // Reset search results
    }
  }, [visible, onSearch]);

  useEffect(() => {
    if (currentAssignedUsers.length > 0) {
      const selectedKeys = currentAssignedUsers.map(user => 
        typeof user === 'string' ? user : user._id
      );
      setSelectedRowKeys(selectedKeys);
      setInitialSelectedKeys(selectedKeys);
    } else {
      setSelectedRowKeys([]);
      setInitialSelectedKeys([]);
    }
  }, [currentAssignedUsers]);

  // Handle modal cancel with cleanup
  const handleCancel = () => {
    setSearch(''); // Reset search text
    onSearch?.(''); // Reset search results
    onCancel?.(); // Call the original onCancel
  };

  // Xử lý onChange của input search
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    debouncedSearch(value);
  };

  const handleOk = async () => {
    try {
      // Find newly selected users (to assign)
      const usersToAssign = selectedRowKeys.filter(key => !initialSelectedKeys.includes(key));
      
      // Find unselected users (to unassign)
      const usersToUnassign = initialSelectedKeys.filter(key => !selectedRowKeys.includes(key));

      let success = true;

      // Handle unassign first if there are users to unassign
      if (usersToUnassign.length > 0) {
        try {
          await CategoryApiService.unassignCategory(categoryId, {
            userIds: usersToUnassign
          });
        } catch (error) {
          // If it's not a 500 error, mark as failed
          if (error.response?.status !== 500) {
            success = false;
          }
        }
      }

      // Then handle assign if there are users to assign
      if (usersToAssign.length > 0 && success) {
        try {
          await CategoryApiService.assignCategory(categoryId, {
            userIds: usersToAssign
          });
        } catch (error) {
          // If it's not a 500 error, mark as failed
          if (error.response?.status !== 500) {
            success = false;
          }
        }
      }

      if (success) {
        // Get the final list of assigned users
        const finalAssignedUsers = users.filter(user => selectedRowKeys.includes(user._id));
        
        // Show appropriate message based on the actions performed
        if (usersToUnassign.length > 0 && usersToAssign.length > 0) {
          message.success('User assignments updated successfully');
        } else if (usersToUnassign.length > 0) {
          message.success('Users unassigned successfully');
        } else if (usersToAssign.length > 0) {
          message.success('Users assigned successfully');
        }
        
        onOk(finalAssignedUsers);
      } else {
        throw new Error('Failed to update assignments');
      }
    } catch (error) {
      console.error('Error updating user assignments:', error);
      
      // Show appropriate error message based on the actions attempted
      if (error.response?.status === 500) {
        const finalAssignedUsers = users.filter(user => selectedRowKeys.includes(user._id));
        onOk(finalAssignedUsers);
      } else {
        message.error('Failed to update user assignments');
      }
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys) => {
      setSelectedRowKeys(selectedKeys);
    }
  };

  const columns = [
    {
      title: 'Username',
      dataIndex: 'email',
      key: 'email',
      render: (email) => (
        <Space>
          <UserOutlined />
          <span>{email}</span>
        </Space>
      )
    },
    {
      title: 'Fullname',
      key: 'fullname',
      render: (_, record) => `${record.firstName || ''} ${record.lastName || ''}`.trim() || '-'
    },
    {
      title: 'Role',
      key: 'role',
      render: (_, user) => {
        // Chỉ lấy role STAFF_COURSE_MANAGER hoặc STAFF_COURSE_MENTOR
        const staffRole = user.roles?.find(role => 
          role === "STAFF_COURSE_MANAGER" || role === "STAFF_COURSE_MENTOR"
        );

        // Format tên role cho đẹp
        let displayRole = "-";
        if (staffRole === "STAFF_COURSE_MANAGER") {
          displayRole = "Staff Course Manager";
        } else if (staffRole === "STAFF_COURSE_MENTOR") {
          displayRole = "Staff Course Mentor";
        }

        return (
          <span className={styles.userRole}>
            {displayRole}
          </span>
        );
      }
    }
  ];

  // Filter users to only show those with STAFF_COURSE_MANAGER or STAFF_COURSE_MENTOR roles
  const filteredUsers = users.filter(user => 
    user.roles?.some(role => 
      role === "STAFF_COURSE_MANAGER" || role === "STAFF_COURSE_MENTOR"
    )
  );

  return (
    <Modal
      title="Assign Users to Category"
      open={visible}
      onCancel={onCancel}
      width={900}
      className={styles.assignModal}
      footer={null}
      centered
    >
      <div className={styles.modalContainer}>
        <div className={styles.searchContainer}>
          <Search
            placeholder="Search by email or name..."
            allowClear
            value={search}
            onChange={handleSearchChange}
            className={styles.searchField}
            prefix={<SearchOutlined className={styles.searchIcon} />}
          />
        </div>
        
        <div className={styles.tableContainer}>
          <Table
            rowSelection={{
              type: 'checkbox',
              ...rowSelection
            }}
            columns={columns}
            dataSource={filteredUsers}
            rowKey="_id"
            loading={loading}
            className={styles.userTable}
            pagination={{
              pageSize: 8,
              showSizeChanger: false,
              showTotal: (total) => `Total ${total} items`
            }}
          />
        </div>

        <div className={styles.footerActions}>
          <Button onClick={onCancel} className={styles.cancelButton}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleOk}
            loading={confirmLoading}
            className={styles.saveButton}
          >
            Save Changes
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AssignCategoryModal;